/**
 * 外部カレンダー連携の契約（PRODUCT_DEV-1225）
 *
 * マイ手帳に Google カレンダー / Microsoft 365 の予定を出すための、画面と API の間の型。
 * 処理の本体は `packages/shared-domain/lib/calendarLink/`、Unizo の入口は
 * `apps/unizo/api/lambda/calendar/router.ts`。
 *
 * 決めごと（2026-10-23 合意）:
 * - **読むだけ**。予定は保存せず、開くたびに取りに行く
 * - **1人1連携**（Google か M365 のどちらか）。新しくつなぐと前の連携は外す
 * - 外部の予定は**本人以外に出さない**
 */
import { z } from "zod";

/** 同意画面から戻る先（Unizo の web のルート）。サーバが redirect_uri を組むのにも使う。 */
export const CALENDAR_CALLBACK_PATH = "/settings/calendar/callback";

export const CALENDAR_PROVIDERS = ["google", "m365"] as const;
export const CalendarProviderSchema = z.enum(CALENDAR_PROVIDERS);
export type CalendarProvider = z.infer<typeof CalendarProviderSchema>;

/** どのアプリでつないだか。テナントの自社アプリ（上書き）か、当社の配布用アプリか。 */
export const CalendarAppSourceSchema = z.enum(["tenant", "distributed"]);
export type CalendarAppSource = z.infer<typeof CalendarAppSourceSchema>;

/** 画面に出す連携の状態。**トークンや鍵は一切含めない。** */
export const CalendarConnectionSchema = z.object({
  provider: CalendarProviderSchema,
  /** つないだアカウント（Google のメール / M365 の UPN）。 */
  accountEmail: z.string(),
  appSource: CalendarAppSourceSchema,
  connectedAt: z.string(),
  /** 相手側で取り消された・期限が切れたなど、つなぎ直しが要る。 */
  reauthRequired: z.boolean(),
});
export type CalendarConnection = z.infer<typeof CalendarConnectionSchema>;

export const CalendarConnectionResponseSchema = z.object({
  connection: CalendarConnectionSchema.nullable(),
  /** このテナントで、いまつなげる先。アプリが用意されていなければ false。 */
  available: z.object({ google: z.boolean(), m365: z.boolean() }),
  /**
   * 管理者が会社として使うようにしているか（テナント設定の「外部連携」）。既定はどちらも false。
   * `available` はこれと「アプリが用意されているか」の両方が真のときだけ真になる。
   */
  tenantSettings: z.object({ googleEnabled: z.boolean(), m365Enabled: z.boolean() }),
});
export type CalendarConnectionResponse = z.infer<
  typeof CalendarConnectionResponseSchema
>;

export const CalendarConnectStartRequestSchema = z.object({
  provider: CalendarProviderSchema,
  /**
   * 戻り先の土台（origin＋アプリの置き場所。例: `https://x.devserver.dev.umeee-app.com/vite/unizo`）。
   * **dev の開発サーバだけ**のための口で、origin がサーバの許可リストに無ければ無視して
   * 既定（そのアプリの本来の URL）に戻す。
   */
  redirectBase: z.string().max(300).optional(),
});
export type CalendarConnectStartRequest = z.infer<
  typeof CalendarConnectStartRequestSchema
>;

export const CalendarConnectStartResponseSchema = z.object({
  authUrl: z.string(),
  /** ブラウザ側でも戻ってきた state と突き合わせる（二重の確認）。 */
  state: z.string(),
});
export type CalendarConnectStartResponse = z.infer<
  typeof CalendarConnectStartResponseSchema
>;

export const CalendarConnectFinishRequestSchema = z.object({
  state: z.string().min(16).max(200),
  code: z.string().min(1).max(4000),
});
export type CalendarConnectFinishRequest = z.infer<
  typeof CalendarConnectFinishRequestSchema
>;

/**
 * つなぐのに失敗した理由。画面はこれで案内を出し分ける。
 * - `state`: 期限切れ・別の人・二度目（もう一度「つなぐ」から）
 * - `scope-missing`: 同意画面でカレンダーのチェックを外した
 * - `consent-required`: 顧客の管理者の承認が要る（M365）
 * - `unavailable`: このテナントでは使えるアプリが無い
 * - `provider`: 相手側のエラー
 */
export const CalendarConnectErrorReasonSchema = z.enum([
  "state",
  "scope-missing",
  "consent-required",
  "unavailable",
  "provider",
]);
export type CalendarConnectErrorReason = z.infer<
  typeof CalendarConnectErrorReasonSchema
>;

export const CalendarDisconnectResponseSchema = z.object({
  disconnected: z.literal(true),
  /** 相手側の許可まで取り消せたか。M365 は取り消す API が無いので常に false（画面で外し方を案内する）。 */
  providerRevoked: z.boolean(),
});
export type CalendarDisconnectResponse = z.infer<
  typeof CalendarDisconnectResponseSchema
>;

/** 1回で読む幅の上限（日）。手帳は月単位で読むので 1 か月＋前後が入れば足りる。 */
export const CALENDAR_EVENTS_MAX_DAYS = 62;

/**
 * 外部予定のキー。商談記録に「どの予定から録ったか」として残す（PR4）。
 *
 * カレンダーの部分は **`primary` 固定**。Google の primary の ID はメールアドレスなので、
 * テナント全員が見られる記録に載せないため。読むのはメインのカレンダーだけなので一意に決まる。
 *
 * ⚠ **Unizo の記録の受け口（`unizo/contract.ts` の商談記録・自由記録・議事録）もこの形で検査している。**
 * 狭めると、合わない値は 400 にならず黙って捨てられ、録った記録が予定とつながらなくなる。
 */
export const EXTERNAL_EVENT_KEY_RE = /^(google|m365):primary:[A-Za-z0-9_\-+/=]{1,1000}$/;

export const ExternalCalendarAttendeeSchema = z.object({
  name: z.string().optional(),
  email: z.string(),
});

export const ExternalCalendarEventSchema = z.object({
  key: z.string(),
  provider: CalendarProviderSchema,
  eventId: z.string(),
  /** 重複のまとめに使う（日程調整の控えメールの .ics の UID と突き合わせる）。 */
  iCalUid: z.string().optional(),
  title: z.string(),
  allDay: z.boolean(),
  /** 時刻つきの予定。JST の ISO（`+09:00`）。 */
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  /** 終日の予定。`YYYY-MM-DD`。`endDate` は**含まない**（Google・M365 と同じ）。 */
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  location: z.string().optional(),
  attendees: z.array(ExternalCalendarAttendeeSchema),
  /** Meet / Teams などの参加 URL。 */
  meetingUrl: z.string().optional(),
  /** 元のカレンダーで開く URL。 */
  openUrl: z.string().optional(),
  isPrivate: z.boolean(),
  /** 「空き時間」として入れた予定（Google の transparent / M365 の showAs=free）。 */
  isFree: z.boolean(),
});
export type ExternalCalendarEvent = z.infer<typeof ExternalCalendarEventSchema>;

/**
 * 予定が読めなかった理由。
 * - `reauth`: つなぎ直しが要る
 * - `throttled`: 相手側の上限。少し待つ
 * - `provider-error`: 相手側のエラー
 * - `consent-required`: 会社の管理者が許可を外した（M365）。管理者の承認のあと、つなぎ直す
 */
export const CalendarEventsErrorReasonSchema = z.enum([
  "reauth",
  "throttled",
  "provider-error",
  "consent-required",
]);
export type CalendarEventsErrorReason = z.infer<
  typeof CalendarEventsErrorReasonSchema
>;

export const CalendarEventsResponseSchema = z.union([
  z.object({ connected: z.literal(false) }),
  z.object({
    connected: z.literal(true),
    provider: CalendarProviderSchema,
    events: z.array(ExternalCalendarEventSchema),
  }),
  z.object({
    connected: z.literal(true),
    provider: CalendarProviderSchema,
    error: CalendarEventsErrorReasonSchema,
  }),
]);
export type CalendarEventsResponse = z.infer<typeof CalendarEventsResponseSchema>;

/**
 * テナント設定の「外部連携 → 外部カレンダー」（GET /admin/tenant-settings/calendar・管理者だけ）。
 */
export interface CalendarTenantSettingsView {
  tenantSettings: { googleEnabled: boolean; m365Enabled: boolean };
  /** 当社のアプリ（かテナントの自社アプリ）が用意されているか。無ければ「準備中」。 */
  appsReady: { google: boolean; m365: boolean };
  /** テナントの自社 Google アプリが登録されているか。 */
  tenantGoogleApp: { configured: boolean; clientId: string };
}

/**
 * 管理者が会社として使うかを切り替える（PUT /admin/tenant-settings/calendar・管理者だけ）。
 * 送ったものだけ変える。**false にしたほうは、つないでいる社員の連携をすべて外す**（つなぎ直しが要る）。
 */
export const PutCalendarTenantSettingsRequestSchema = z
  .object({ googleEnabled: z.boolean(), m365Enabled: z.boolean() })
  .partial();
export type PutCalendarTenantSettingsRequest = z.infer<
  typeof PutCalendarTenantSettingsRequestSchema
>;

export interface PutCalendarTenantSettingsResponse {
  tenantSettings: { googleEnabled: boolean; m365Enabled: boolean };
  /** 外した連携の数。 */
  disconnected: number;
}

/** テナントの自社 Google アプリの登録（管理者だけ）。値は既存の日程調整の GCALAPP に入る。 */
export const PutTenantGoogleAppRequestSchema = z.object({
  clientId: z.string().trim().min(10).max(400),
  clientSecret: z.string().trim().min(10).max(400),
});
export type PutTenantGoogleAppRequest = z.infer<
  typeof PutTenantGoogleAppRequestSchema
>;

/**
 * 片付けの Lambda（`UnizoCalendarCleanup-<env>`）へ渡す中身（確認の指摘1）。API Gateway は通らず、
 * Unizo のユーザ管理（無効にする・完全に削除）と Admin のプロダクト保存（フラグ OFF・無効化）が直接呼ぶ。
 * 受けた側は今の状態を読み直してから外す（この中身だけで外さない）。
 */
const CleanupIdSchema = z.string().min(1).max(128).regex(/^[A-Za-z0-9_-]+$/);
export const CalendarCleanupRequestSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), tenantId: CleanupIdSchema, userId: CleanupIdSchema }),
  z.object({ kind: z.literal("tenant"), tenantId: CleanupIdSchema }),
]);
export type CalendarCleanupRequest = z.infer<typeof CalendarCleanupRequestSchema>;
